/**
 * A tiny, safe arithmetic language for formulas an assistant writes into a
 * reply (a what-if calculator): numbers, input names, + - * /, unary minus,
 * parentheses, and min, max, round, abs, ceil, floor. It is parsed by hand
 * and never handed to eval or Function. The Go twin
 * (services/shanks/pkg/visuals/formula.go) checks the same grammar.
 */ const MAX_LENGTH = 500;
const MAX_DEPTH = 32;
const FUNCTIONS = {
    min: [
        1,
        Number.POSITIVE_INFINITY
    ],
    max: [
        1,
        Number.POSITIVE_INFINITY
    ],
    round: [
        1,
        2
    ],
    abs: [
        1,
        1
    ],
    ceil: [
        1,
        1
    ],
    floor: [
        1,
        1
    ]
};
class FormulaError extends Error {
}
function tokenize(src) {
    const out = [];
    let i = 0;
    while(i < src.length){
        const c = src[i];
        if (c === ' ' || c === '\t' || c === '\n' || c === '\r') {
            i++;
            continue;
        }
        if (/[0-9.]/.test(c)) {
            const m = /^(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?/.exec(src.slice(i));
            if (!m) throw new FormulaError(`bad number at ${i + 1}`);
            out.push({
                t: 'num',
                v: Number(m[0]),
                at: i + 1
            });
            i += m[0].length;
            continue;
        }
        if (/[A-Za-z_]/.test(c)) {
            const m = /^[A-Za-z_][A-Za-z0-9_]*/.exec(src.slice(i));
            out.push({
                t: 'id',
                v: m[0],
                at: i + 1
            });
            i += m[0].length;
            continue;
        }
        if ('+-*/(),'.includes(c)) {
            out.push({
                t: 'op',
                v: c,
                at: i + 1
            });
            i++;
            continue;
        }
        throw new FormulaError(`unexpected "${c}" at ${i + 1}`);
    }
    out.push({
        t: 'end',
        at: src.length + 1
    });
    return out;
}
function parse(src) {
    if (src.length > MAX_LENGTH) throw new FormulaError(`formula longer than ${MAX_LENGTH} characters`);
    if (src.trim() === '') throw new FormulaError('empty formula');
    const toks = tokenize(src);
    let p = 0;
    let depth = 0;
    const peek = ()=>toks[p];
    const isOp = (v)=>{
        const t = toks[p];
        return t.t === 'op' && t.v === v;
    };
    const describe = (t)=>t.t === 'end' ? 'end of formula' : `"${t.t === 'num' ? String(t.v) : t.v}" at ${t.at}`;
    const enter = ()=>{
        depth++;
        if (depth > MAX_DEPTH) throw new FormulaError('formula nested too deeply');
    };
    const expr = ()=>{
        enter();
        let a = term();
        while(isOp('+') || isOp('-')){
            const op = toks[p++].v;
            a = {
                k: 'bin',
                op,
                a,
                b: term()
            };
        }
        depth--;
        return a;
    };
    const term = ()=>{
        let a = unary();
        while(isOp('*') || isOp('/')){
            const op = toks[p++].v;
            a = {
                k: 'bin',
                op,
                a,
                b: unary()
            };
        }
        return a;
    };
    const unary = ()=>{
        if (isOp('-')) {
            p++;
            enter();
            const a = unary();
            depth--;
            return {
                k: 'neg',
                a
            };
        }
        if (isOp('+')) {
            p++;
            return unary();
        }
        return primary();
    };
    const primary = ()=>{
        const t = peek();
        if (t.t === 'num') {
            p++;
            return {
                k: 'num',
                v: t.v
            };
        }
        if (t.t === 'id') {
            p++;
            if (isOp('(')) {
                const arity = FUNCTIONS[t.v];
                if (!arity) throw new FormulaError(`unknown function "${t.v}"`);
                p++;
                const args = [];
                if (!isOp(')')) {
                    args.push(expr());
                    while(isOp(',')){
                        p++;
                        args.push(expr());
                    }
                }
                if (!isOp(')')) throw new FormulaError(`expected ")" but found ${describe(peek())}`);
                p++;
                if (args.length < arity[0] || args.length > arity[1]) {
                    throw new FormulaError(`${t.v}() takes ${arity[0] === arity[1] ? arity[0] : `${arity[0]}+`} argument(s)`);
                }
                return {
                    k: 'call',
                    fn: t.v,
                    args
                };
            }
            return {
                k: 'var',
                name: t.v
            };
        }
        if (isOp('(')) {
            p++;
            const a = expr();
            if (!isOp(')')) throw new FormulaError(`expected ")" but found ${describe(peek())}`);
            p++;
            return a;
        }
        throw new FormulaError(`unexpected ${describe(t)}`);
    };
    const root = expr();
    if (peek().t !== 'end') throw new FormulaError(`unexpected ${describe(peek())}`);
    return root;
}
function collectVars(n, out) {
    switch(n.k){
        case 'var':
            out.add(n.name);
            break;
        case 'neg':
            collectVars(n.a, out);
            break;
        case 'bin':
            collectVars(n.a, out);
            collectVars(n.b, out);
            break;
        case 'call':
            for (const a of n.args)collectVars(a, out);
            break;
    }
}
/** Checks a formula; with `known`, every name must be one of them. */ export function checkFormula(expr, known) {
    try {
        const vars = new Set();
        collectVars(parse(expr), vars);
        if (known) {
            for (const v of vars)if (!known.has(v)) return {
                ok: false,
                error: `unknown input "${v}"`
            };
        }
        return {
            ok: true,
            vars: [
                ...vars
            ]
        };
    } catch (e) {
        return {
            ok: false,
            error: e instanceof FormulaError ? e.message : 'invalid formula'
        };
    }
}
function evalNode(n, vars) {
    switch(n.k){
        case 'num':
            return n.v;
        case 'var':
            return Object.getOwnPropertyDescriptor(vars, n.name) ? Number(vars[n.name]) : Number.NaN;
        case 'neg':
            return -evalNode(n.a, vars);
        case 'bin':
            {
                const a = evalNode(n.a, vars);
                const b = evalNode(n.b, vars);
                if (n.op === '+') return a + b;
                if (n.op === '-') return a - b;
                if (n.op === '*') return a * b;
                return b === 0 ? Number.NaN : a / b;
            }
        case 'call':
            {
                const args = n.args.map((a)=>evalNode(a, vars));
                switch(n.fn){
                    case 'min':
                        return Math.min(...args);
                    case 'max':
                        return Math.max(...args);
                    case 'abs':
                        return Math.abs(args[0]);
                    case 'ceil':
                        return Math.ceil(args[0]);
                    case 'floor':
                        return Math.floor(args[0]);
                    case 'round':
                        {
                            const digits = args.length > 1 ? Math.trunc(args[1]) : 0;
                            if (!Number.isFinite(digits) || Math.abs(digits) > 12) return Number.NaN;
                            const f = 10 ** digits;
                            return Math.round(args[0] * f) / f;
                        }
                }
                return Number.NaN;
            }
    }
}
/** The formula's value; NaN on any error (bad formula, missing input, division by zero). */ export function evaluateFormula(expr, vars) {
    try {
        const v = evalNode(parse(expr), vars);
        return Number.isFinite(v) ? v : Number.NaN;
    } catch  {
        return Number.NaN;
    }
}
